import { useState, useRef } from 'react';
import { X, Upload, FileText, Image, Trash2 } from 'lucide-react';
import { createProject, updateProject, deleteProjectFile, type ProjectFile, type Project } from '../projects';
import './ProjectEditModal.css';

type ProjectEditModalProps = {
    mode: 'create' | 'edit';
    projectId?: string;
    name?: string;
    description?: string;
    files?: ProjectFile[];
    onClose: () => void;
    onSaved: (project: { id: string; name: string; description: string; files: ProjectFile[] }) => void;
};

export default function ProjectEditModal({ mode, projectId, name = '', description = '', files = [], onClose, onSaved }: ProjectEditModalProps) {
    const [editName, setEditName] = useState(name);
    const [editDescription, setEditDescription] = useState(description);
    const [currentFiles, setCurrentFiles] = useState<ProjectFile[]>(files);
    const [newFiles, setNewFiles] = useState<File[]>([]);
    const [saving, setSaving] = useState(false);
    const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setNewFiles(prev => [...prev, ...Array.from(e.target.files!)]);
        }
    };

    const handleRemoveNewFile = (index: number) => {
        setNewFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleDeleteExistingFile = async (fileId: string) => {
        setDeletingFileId(fileId);
        try {
            await deleteProjectFile(fileId);
            setCurrentFiles(prev => prev.filter(f => f.id !== fileId));
        } catch (error) {
            console.error('Failed to delete file:', error);
        }
        setDeletingFileId(null);
    };

    const handleSave = async () => {
        if (!editName.trim()) return;
        setSaving(true);
        try {
            let result: Project;
            if (mode === 'create') {
                result = await createProject(
                    editName.trim(),
                    editDescription.trim(),
                    newFiles.length > 0 ? newFiles : undefined
                );
            } else {
                result = await updateProject(
                    projectId!,
                    editName.trim(),
                    editDescription.trim(),
                    newFiles.length > 0 ? newFiles : undefined
                );
            }
            onSaved({
                id: result.id,
                name: result.name,
                description: result.description,
                files: result.files || currentFiles,
            });
            onClose();
        } catch (error) {
            console.error(`Failed to ${mode} project:`, error);
        }
        setSaving(false);
    };

    const getFileIcon = (fileType: string) => {
        if (fileType.startsWith('image/')) return <Image size={16} />;
        return <FileText size={16} />;
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="project-edit-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{mode === 'create' ? 'New Project' : 'Edit Project'}</h2>
                    <button type="button" className="modal-close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="form-field">
                        <label>Project Name</label>
                        <input
                            type="text"
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            placeholder="Project name"
                            className="form-input"
                        />
                    </div>

                    <div className="form-field">
                        <label>Description</label>
                        <textarea
                            value={editDescription}
                            onChange={e => setEditDescription(e.target.value)}
                            placeholder="Describe this project..."
                            className="form-textarea"
                            rows={3}
                        />
                    </div>

                    {/* Existing Files */}
                    {currentFiles.length > 0 && (
                        <div className="form-field">
                            <label>Files</label>
                            <div className="file-list">
                                {currentFiles.map(file => (
                                    <div key={file.id} className="file-item">
                                        <span className="file-icon">{getFileIcon(file.file_type)}</span>
                                        <span className="file-name">{file.original_name}</span>
                                        <button
                                            type="button"
                                            className="file-delete"
                                            onClick={() => handleDeleteExistingFile(file.id)}
                                            disabled={deletingFileId === file.id}
                                        >
                                            {deletingFileId === file.id ? '...' : <Trash2 size={14} />}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* New Files */}
                    {newFiles.length > 0 && (
                        <div className="form-field">
                            <label>New Files</label>
                            <div className="file-list">
                                {newFiles.map((file, i) => (
                                    <div key={i} className="file-item file-item--new">
                                        <span className="file-icon"><FileText size={16} /></span>
                                        <span className="file-name">{file.name}</span>
                                        <button type="button" className="file-delete" onClick={() => handleRemoveNewFile(i)}>
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Upload Button */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleAddFiles}
                        style={{ display: 'none' }}
                    />
                    <button
                        type="button"
                        className="upload-btn"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <Upload size={16} />
                        Add Files
                    </button>
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
                    <button
                        type="button"
                        className="btn-save"
                        onClick={handleSave}
                        disabled={saving || !editName.trim()}
                    >
                        {saving ? (mode === 'create' ? 'Creating...' : 'Saving...') : (mode === 'create' ? 'Create' : 'Save')}
                    </button>
                </div>
            </div>
        </div>
    );
}
